console.debug("LOADING SourceChannelReplay")
import { action, flow, observable, makeObservable } from "mobx"
import { moment } from "store/util/moment"
import { TIME_FORMAT_BACKEND } from "store/model/Time"
//import { SourceTypeList, SourceAbstract } from './InternalImports'
import { SourceAbstract } from "store/page/player/source/SourceAbstract"
import { SourceTypeList } from "store/page/player/source/SourceTypeList"
import { rootStore } from "store/RootStore"
//import { serviceUrls } from "store/qlapi/ServiceUrls"
import { logLevel } from "store/qlapi/LogService"
import { cDebug } from "utils/TestSupport"
import { appFeatures } from "AppFeatures"
import { isIphone, sleep } from "utils/Utils"
import { startTs, endTs } from "utils/EventHelpers"

export class SourceChannelReplay extends SourceAbstract {
	type = SourceTypeList.ChannelReplay
	time = null

	constructor(parent, path) {
		super(parent, path)
		makeObservable(this, {
			time: observable,
			getClone: action,
			setPlayPause: action,
			setJumpLive: action,
			setJumpRwd: action,
			setJumpFwd: action,
			setReplay: action
		})
		//console.debug("constructor SourceChannelReplay %o, %o", parent.player, path)
		this._player = parent.player
	}

	/**
	 * @returns {SourceChannelReplay}
	 */
	static create(parent, channelId, oEventFull, bufferTimeStart, trueReplaySession = true, sessionId = null, restartDone = false) {
		console.log("@@create(parent=%o, channelId=%o, oEventFull=%o, bufferTimeStart=%o)", parent, channelId, oEventFull, bufferTimeStart)
		const source = new SourceChannelReplay(parent, "source")
		source.id = channelId
		// MTVW-859: replace traxis source.Channel
		source.channelId = channelId
		source.GetEvent = oEventFull
		// would create moment.js warning
		//source.time = String(bufferTimeStart)
		source.time = moment(bufferTimeStart).utc().format(TIME_FORMAT_BACKEND)
		console.debug("create SourceChannelReplay:", bufferTimeStart, source.time)
		source.trueReplaySession = trueReplaySession
		source.sessionId = sessionId
		source.restartDone = restartDone
		//console.debug("---SourceChannelReplay CREATE %o, %o, %o", source, oEventFull, parent)
		return source
	}

	/**
	 * @returns {SourceChannelReplay}
	 */
	getClone() {
		const source = new SourceChannelReplay(this._parent, "source")
		source.id = this.id
		// MTVW-859: replace traxis source.Channel
		source.channelId = this.channelId
		source.GetEvent = this.GetEvent
		source.time = this.time
		source.trueReplaySession = this.trueReplaySession
		source.sessionId = this.sessionId
		source.restartDone = this.restartDone
		source.isLoading = false
		source.adState = this.adState
		source.sessionAdZones = this.sessionAdZones
		source.sessionEnd = this.sessionEnd
		//console.debug("getClone", source)
		return source
	}

	isSourceEqual(source) {
		return this.type === source.type && this.id === String(source.id) && this.time === String(source.time)
	}

	get isLive() {
		return false
	}

	// buttons disable state
	get isButtonLiveEnabled() {
		// only when we are not in live range
		return this.player.isStreamLoaded
	}

	get isButtonReplayEnabled() {
		return this.player.isStreamLoaded
	}

	get isButtonJumpFwdEnabled() {
		return this.player.isJumpFwdPossibleSec(this.settings.jumpFwdSec)
		//return false
	}

	get isButtonJumpRwdEnabled() {
		return this.player.isJumpRwdPossibleSec(this.settings.jumpRwdSec)
		//return false
	}

	get isButtonPlayPauseEnabled() {
		return this.player.isStreamLoaded
	}

	get isButtonRecEnabled() {
		return this.player.isStreamLoaded && appFeatures.traxisAvailable
	}

	openStreamAsync = flow(function* (startPosition = null) {
		console.debug("@@Replay openStreamAsync() -> ", this.time, this)
		this._waitPreload.setReset()
		// OPTIMIZATION for SourceChannelReplay.setReplay (currently only for non GT12 channels)
		// MTVW-1096: replay stream start performance improvement
		//yield this._openStreamRollingBufferAsync(startTs(this.GetEvent), startPosition ? startPosition : this.time, endTs(this.GetEvent))
		yield this._openReplayStreamAsync(this.GetEvent, startPosition ?? this.time)
		console.info("@@openStreamAsync() <- ")
	})

	// MTVW-543: use handleSetPosition for bufferStart / bufferEnd detection
	handleStreamOnEnd = flow(function* (positionTimeStamp) {
		const source = this.getClone().setAsLoaded()
		// MTVW-678: parameter positionTimeStamp, set source.time
		source.time = moment(positionTimeStamp).utc().format(TIME_FORMAT_BACKEND)
		console.debug("handleStreamOnEnd", source, positionTimeStamp)
		// GT12: Fix old existing bug, need to use 'source' for callback and not 'this
		// MTVW-822, MTVW-823: added endTs(source.Event))
		// MTVW-1096: replay stream start performance improvement
		//yield this._parent.handlePlaySourcePreserveProgressBarAsync(source, (source) => source._openStreamRollingBufferAsync(startTs(source.Event), moment(positionTimeStamp), endTs(source.Event)))
		yield this._parent.handlePlaySourcePreserveProgressBarAsync(source, (source) => source._openReplayStreamAsync(source.Event, moment(positionTimeStamp) ))
	})

	//setPlayPause() {
	setPlayPause = flow(function* () {
		//if (!this.debounced()) return
		//console.debug("setPlayPause SourceChannelReplay", this.player.isPaused, this.player, this.player.__parent.playerFg)
		if (!this.player.isPaused) {
			this.player.setPause()
		} else {
			this.removePauseAd()
			console.debug("setPlayPause replay", this._parent.playerFg, this._parent.player)
			// MTVW-444, MTVW-788, MTVW-787
			if (this._parent.playerFg) {
				yield this._waitPreload.waitAsync()
				// if VITE_DISPOSE_LIVE_PLAYER playerFg will be cleared on preload
				if (!(import.meta.env.VITE_DISPOSE_LIVE_PLAYER === "true")) {
					// swap players and switch to background player
					yield this._parent.swapPlayers()
				}
				console.debug("setPlayPause replay 1", this._parent.playerFg, this._parent.player)
			}
			yield this.player.setPlay("setPlayPause")
		}
		return this
	})

	setJumpLive = flow(function* () {
		cDebug("SourceChannelReplay setJumpLive", this, this.player, this.player?.positionTimeStamp)
		if (import.meta.env.VITE_PLAYER === "vjs") {
			//yield this.player.setRefElemContainer(null, isIphone() ? false : true)
			//yield this._parent.playerAds.setRefElemContainer(null, isIphone() ? false : true)
			//yield this.player.setRefElemContainer(null, true)
			//yield this._parent.playerAds.setRefElemContainer(null, true)
			//yield this._parent.remountPlayerComponent()
		}
		// NOTE: VITE_DISPOSE_LIVE_PLAYER is true
		if (this._parent.playerFg && !(import.meta.env.VITE_DISPOSE_LIVE_PLAYER === "true")) {
			// MTVW-444, MTVW-788, MTVW-787: we can jump to live using the existing live source
			// keep foreground player
			yield this._parent.deactivateBgPlayer()
			yield this._parent.player._source.setJumpLive()
		}
		else {
			if (this._parent.playerFg) {
				yield this._parent.inactivePlayer.stopInstance()
				this._parent.setPlayerFg(null)
			}
			yield this._parent.player.stopInstance()
			// MTVW-1336: getEventByChannelTime in case we are not at current live source
			this.GetEvent = yield rootStore.page.MsEpg.getEventByChannelTime(this.channelId, moment().utc().valueOf())
			// setPlayChannelLiveAsync will create a bookmark via interactionEvent
			yield this._parent.setPlayChannelLiveAsync(this.channelId, this.GetEvent, false, this.restartDone)
		}
	})

	//actionAfterFastForwardAd = (closeInteraction = "") => {
	actionAfterFastForwardAd = flow(function* (closeInteraction = "", timeStamp, playTime, playCompleted) {
		try {
			//console.debug("actionAfterFastForwardAd")
			// MTVW-544: suppress "Überspringen" after ffad
			this.markerPosD = Number(this.adZone?.end)
			if (this.adState?.linear) this.adState.linear.skip = false
			const playbackTime = this.player.playbackTime ? Math.round(this.player.playbackTime * 1000) : 0
			console.debug("JUMPTO playbackTime %s, durationMetaData %s", playbackTime, this.player.durationMetaData * 1000)
			yield this.exitAdsPlayer()
			const jumpTo = this.fastForwardAdInfo?.jumpTo ? Number(this.fastForwardAdInfo?.jumpTo) : null
			if (jumpTo) cDebug("JUMPTO %s", moment(jumpTo).utc().format(TIME_FORMAT_BACKEND), timeStamp, playTime, playCompleted)
			const from = this.player.positionTimeStamp
			// MTVW-1062: on @CAN, the player got stalled in the seeking state when jumpTo was very close (159 ms) to the current position
			if (jumpTo && Math.abs(moment(jumpTo).utc().valueOf() - from) > 500) {
				yield this.player.setPositionTimeStampAsync(jumpTo)
			}
			else console.debug("skip setPositionTimeStampAsync")
			if (this.player.isPaused) yield this.player.setPlay("actionAfterFastForwardAd")
			this.player.setIsPosterVisible(false, "actionAfterFastForwardAd")
			// GT12 TODO: special cases
			// closeInteraction: [ changeAsset, interruption, skipPromo ] or "" for no interaction
			// error: [ adNotFound, other ] or ""  for no error
			//console.debug("AFTER FASTFORWARD", moment(this.adPlayStart).utc().format(TIME_FORMAT_BACKEND), moment().utc().format(TIME_FORMAT_BACKEND))
			rootStore.adsService.sessionTrackingFastForwardAdAsync(this.sessionId, this.fastForwardAdInfo.fastForwardAd.adsViewId, playbackTime, closeInteraction, "")
			// MTVW-649: disable notifyJump
			/*
			if (from !== Number(this.fastForwardAdInfo.jumpTo))
				rootStore.adsService.sessionNotifyJumpAsync(this.sessionId, startTs(this.Event), endTs(this.Event), from, Number(this.fastForwardAdInfo.jumpTo))
			*/
		}
		catch (e) {
			console.error("actionAfterFastForwardAd", e)
			rootStore.logService.logAsync(logLevel.Error, "actionAfterFastForwardAd", "SourceChannelReplay", e)
		}
	})

	// MTVW-543: use handleSetPosition for bufferStart / bufferEnd detection
	setJumpRwd = flow(function* () {
		//console.debug("setJumpRwd", this.player.positionTimeStamp, this.player)
		//  MTVW-444, MTVW-788, MTVW-787
		if (this._parent.playerFg) {
			console.debug("setJumpRwd", this.player.positionTimeStamp, this._parent.playerFg, this._waitPreload.isDone)
			// swap players and switch to background player
			// MTVW-444, MTVW-788, MTVW-787
			yield this._waitPreload.waitAsync()
			// if VITE_DISPOSE_LIVE_PLAYER playerFg will be cleared on preload
			//console.debug("setJumpRwd paused", this._parent.playerFg.isPaused, this.player, this.player.isPaused)
			//console.debug("setJumpRwd fg", this._parent.playerFg?.isPaused, this.player.positionTimeStamp, this._parent.playerFg?.positionTimeStamp)
			if (!(import.meta.env.VITE_DISPOSE_LIVE_PLAYER === "true")) {
				//yield this._parent.playerFg.setPositionTimeStamp(timeStamp - this.settings.jumpFwdSec * 1000)
				yield this._parent.swapPlayers()
			}
			// let _startReplaySession position first, added for setting VITE_DISPOSE_LIVE_PLAYER
			yield sleep(200)
			yield this._parent.player.setPositionTimeStampAsync(this.player.positionTimeStamp - this.settings.jumpFwdSec * 1000)
			return
		}
		//console.debug("setJumpRwd", this.player.positionTimeStamp)
		//console.debug("setJumpRwd", this.player, this.player.isPaused)
		yield this.handleSetPosition(moment(this.player.positionTimeStamp - this.settings.jumpFwdSec * 1000))
	})

	_old_setJumpRwd() {
		// jump rwd button

		cDebug("@  source.setJumpRwd")
		if (this._checkStreamRwd()) {
			//this.removePauseAd()
			cDebug("positionTimeStamp %s, %o", this.player.positionTimeStamp, this.player)
			//this.player.setJumpRwd()
			this.handleFastForwardAd(this.player.positionTimeStamp, this.player.positionTimeStamp - this.settings.jumpRwdSec * 1000, this.actionAfterFastForwardAd)
		}
	}

	// MTVW-543: use handleSetPosition for bufferStart / bufferEnd detection
	setJumpFwd = flow(function* () {
		//console.debug("handleSetPosition", this.settings.jumpFwdSec, this.player.positionTimeStamp, moment(this.player.positionTimeStamp + this.settings.jumpFwdSec * 1000))
		//  MTVW-444, MTVW-788, MTVW-787
		if (this._parent.playerFg) {
			// fwd button is disabled
		}
		yield this.handleSetPosition(moment(this.player.positionTimeStamp + this.settings.jumpFwdSec * 1000))
	})

	_old_setJumpFwd() {
		// jump fwd button

		//this.removePauseAd()
		if (this.isButtonJumpFwdEnabled) {
			cDebug("positionTimeStamp %s, %o", this.player.positionTimeStamp, this.player)
			// this.player.setJumpFwd()
			this.handleFastForwardAd(this.player.positionTimeStamp, this.player.positionTimeStamp + this.settings.jumpFwdSec * 1000, this.actionAfterFastForwardAd)
		} else {
			// GT12 TODO ?
			/*
			const isAd = this.handleFastForwardAd(() => {
				this.exitAdsPlayer()
				//this.setPlayPause()
				this._parent.setPlayChannelLiveAsync(this.channelId, this.GetEvent, false, this.restartDone)
			})
			if (!isAd) this._parent.setPlayChannelLiveAsync(this.channelId, this.GetEvent, false, this.restartDone)
			*/
			this._parent.setPlayChannelLiveAsync(this.channelId, this.GetEvent, false, this.restartDone)
		}
	}

	setReplay = flow(function* () {
		// restart button
		//rootStore.page.CoCoBookmarks.setIgnoreEvent(this.Event)
		//console.debug("@gt12 source.setReplay", startTs(this.Event), this.player.rangeTimeStampStart, this.restartDone)
		//this.removePauseAd()
		//console.debug("setReplay", this.player)
		//  MTVW-444, MTVW-788, MTVW-787
		if (this._parent.playerFg && !(import.meta.env.VITE_DISPOSE_LIVE_PLAYER === "true")) {
			// keep foreground player
			yield this._parent.deactivateBgPlayer()
		}
		else if (this._parent.playerFg) {
			//this.removePauseAd("close")
			yield this._waitPreload.waitAsync()
			this._parent.inactivePlayer._source.removePauseAd()
			this.removePauseAd()
			yield this._parent.inactivePlayer.stopInstance()
			//this._parent.setPlayerFg(null)
		}

		if (startTs(this.Event) <= this.player.rangeTimeStampStart) {
			console.debug("setReplay 1", this.GetEvent)
			if (import.meta.env.VITE_PLAYER === "vjs") {
				//yield this.player.setRefElemContainer(null, true)
				//yield this._parent.playerAds.setRefElemContainer(null, true)
				//yield this._parent.remountPlayerComponent()
			}
			this.player.setIsPosterVisible(true, "setReplay", true)
			yield this._parent.player.stopInstance()
			// MTVW-1020: delete bookmark
			yield rootStore.page.CoCoBookmarks.deleteBookmarkAsync(this.Event)
			yield this._parent.handlePlaySourceAsync(SourceChannelReplay.create(this._parent, this.channelId, this.GetEvent, startTs(this.Event), true, null, this.restartDone).setAsLoaded())
		} else {
			console.debug("setReplay 2 %s, %s, %s", this.sessionId, this.trueReplaySession, this.restartDone)
			console.debug("setReplay isBvChannel", this.channelId, this.isBvChannel(this.channelId))
			// GT12
			//this.player.setPositionTimeStamp(startTs(this.Event))
			if (this.isBvChannel(this.channelId) && (this.sessionId === null || !this.trueReplaySession)) {
				console.debug("setReplay 2a")
				// channelId, bufferTimeStart, oEventFull = null, bForceLoad = false, trueReplaySession = true, sessionId = null, restartDone = false
				if (import.meta.env.VITE_PLAYER === "vjs") {
					//yield this.player.setRefElemContainer(null, true)
					//yield this._parent.playerAds.setRefElemContainer(null, true)
					//yield this._parent.remountPlayerComponent()
				}
				this.player.setIsPosterVisible(true, "setReplay", true)
				yield this._parent.player.stopInstance()
				// MTVW-1020: delete bookmark
				yield rootStore.page.CoCoBookmarks.deleteBookmarkAsync(this.Event)
				yield this._parent.setPlayChannelReplayAsync(this.channelId, startTs(this.Event), this.GetEvent, true, true, null, this.restartDone).then(() => {
					//this.player.setPositionTimeStamp(startTs(this.Event)).setPlay("SourceChannelReplay.setReplay")
					//console.debug("restartDone %s", this.restartDone)
				})
			}
			else {
				console.debug("setReplay 2b %s", this.sessionStart)
				// MTVW-495
				//this.player.setPositionTimeStamp(startTs(this.Event))
				// VO PLAYER SYNC
				//if (import.meta.env.VITE_PLAYER === "vjs") {
				if (!this.player.isPaused) this.player.setIsPosterVisible(true, "setReplay", false)
				// OPTIMIZATION for SourceChannelReplay.setReplay (currently only for non GT12 channels)
				if (!this.isBvChannel(this.channelId)) this.sessionStart = null
				//}
				yield this.player.setPositionTimeStampAsync(this.sessionStart ? this.sessionStart : startTs(this.Event))
			}
		}
		this.markerPosD = null
	})

	timeStampEvent(timeStamp, offsetSeconds, playCompleted) {
		this.checkForLinearAd(timeStamp, offsetSeconds)
	}

	// handle click in time bar, called also from skipAd
	handleSetPosition = flow(function* (positionMoment) {
		cDebug("handleSetPosition replay ", positionMoment, this.player.rangeTimeStampStart, this.player.rangeTimeStampEnd /*, new Error().stack*/)
		//this.removePauseAd()

		// MTVW-444, MTVW-788, MTVW-787
		if (this._parent.playerFg) {
			//const fgPaused = this._parent.playerFg.isPaused
			// swap players and switch to background player
			yield this._waitPreload.waitAsync()
			// if VITE_DISPOSE_LIVE_PLAYER playerFg will be cleared on preload
			const diff = this._parent.playerFg ? this._parent.playerFg.positionTimeStamp - moment(positionMoment).utc().valueOf() :
				this._parent.player.positionTimeStamp - moment(positionMoment).utc().valueOf()
			console.debug("diff=", diff)
			//console.debug("handleSetPosition fg", fgPaused, positionMoment, moment(positionMoment).utc().valueOf())
			if (!(import.meta.env.VITE_DISPOSE_LIVE_PLAYER === "true")) {
				//yield this._parent.playerFg.setPositionTimeStamp(moment(positionMoment).utc().valueOf())
				yield this._parent.swapPlayers()
			}
			// let _startReplaySession position first, added for setting VITE_DISPOSE_LIVE_PLAYER
			yield sleep(200)
			//this._parent.player.setPositionTimeStamp(moment(positionMoment).utc().valueOf())
			//console.debug("diff=", this._parent.player.positionTimeStamp, moment(this._parent.player.positionTimeStamp).utc())
			// TODO: We should use handlePlaySourceAsync live setPlayPause in order to jump back more than 15 minutes
			/* possible workaround
			const source = this.getClone().setAsLoaded()
			yield this._parent.handlePlaySourcePreserveProgressBarAsync(source, (source) => source._openStreamRollingBufferAsync(startTs(source.Event), positionMoment))
			*/
			yield this._parent.player.setPositionTimeStampAsync(this._parent.player.positionTimeStamp - diff)
			return
		}

		if (positionMoment.isAfter(moment())) {
			cDebug("handleSetPosition setJumpLive")
			yield this.setJumpLive()
		} else if (positionMoment.isBefore(this.player.rangeTimeStampStart) || (positionMoment.isAfter(this.player.rangeTimeStampEnd) && this.player.isStreamGrowing === false)) {
			cDebug("handleSetPosition special case outside range")
			const source = this.getClone().setAsLoaded()
			const isPaused = this.player.isPaused
			//console.debug("opening player isPaused", isPaused, this.player.isPaused, this.player)
			// GT12: Fix old existing bug, need to use 'source' for callback and not 'this
			yield this._parent.player.stopInstance()
			// MTVW-822, MTVW-823: added endTs(source.Event))
			// MTVW-1096: replay stream start performance improvement
			//yield this._parent.handlePlaySourcePreserveProgressBarAsync(source, (source) => source._openStreamRollingBufferAsync(startTs(source.Event), positionMoment, endTs(source.Event)))
			yield this._parent.handlePlaySourcePreserveProgressBarAsync(source, (source) => source._openReplayStreamAsync(source.Event, positionMoment))
			// MTVW-1020: sometimes we are not a the desired position, so set it again
			yield this.player.setPositionTimeStampAsync(positionMoment.utc().valueOf())
			if (isPaused) this.player.setPause()
		} else {
			cDebug("handleSetPosition positionTimeStamp %s, %o", this.player.positionTimeStamp, this.player)
			//this.player.setPositionTimeStamp(positionMoment)
			yield this.handleFastForwardAd(this.player.positionTimeStamp, moment(positionMoment).utc().valueOf(), this.actionAfterFastForwardAd)
		}
	})
}
